import { useState } from "react";

import { adminSignIn, signIn } from "../lib/api";
import wordmarkWhite from "../assets/polaris-wordmark-white.png";

/** The sign-in screen, shown to anyone not signed in: the Polaris logo and one
 * way in (Google) over a quiet star network, with the admin password sign-in
 * a small link below. Always dark, whatever theme the app is in. */
export function SignIn() {
  const [going, setGoing] = useState(false);
  return (
    <div className="signin" data-theme="dark">
      <StarNetwork />
      <main className="signin-body">
        <p className="signin-kicker">
          <span className="signin-square" aria-hidden />
          Internal tool
        </p>
        <img className="signin-logo" src={wordmarkWhite} alt="Polaris Technologies" />
        <h1>
          <Star className="signin-title-star" />
          Meeting Agent
        </h1>
        <p className="signin-lead">Recordings, transcripts and minutes of your company meetings.</p>

        <button
          className="signin-button"
          onClick={() => {
            setGoing(true);
            signIn();
          }}
          disabled={going}
        >
          <GoogleMark />
          {going ? "Opening Google…" : "Sign in with Google"}
        </button>
        <p className="signin-note">Use your Polaris Google account.</p>

        <div className="signin-strip">
          <span>
            <LockIcon />
            Google Workspace sign-in
          </span>
          <button className="signin-admin" onClick={adminSignIn}>
            Admin sign-in
          </button>
        </div>
        <footer className="signin-footer">© {new Date().getFullYear()} Polaris Technologies</footer>
      </main>
    </div>
  );
}

/** A geodetic network: dotted lines between points, three of them the
 * brand's four-point star, spreading out from the middle of the page. */
function StarNetwork() {
  const nodes: [number, number, number][] = [
    [120, 180, 1.5], [380, 290, 2], [600, 140, 2], [820, 310, 2], [1080, 200, 1.5],
    [490, 520, 2.5], [600, 440, 2], [710, 520, 2.5], [310, 720, 1.5], [600, 780, 2], [890, 720, 1.5],
  ];
  const lines: [number, number, number, number][] = [
    [120, 180, 380, 290], [380, 290, 600, 140], [600, 140, 820, 310], [820, 310, 1080, 200],
    [380, 290, 490, 520], [600, 140, 600, 440], [820, 310, 710, 520], [490, 520, 710, 520],
    [490, 520, 310, 720], [710, 520, 890, 720], [600, 440, 600, 780], [310, 720, 600, 780], [600, 780, 890, 720],
  ];
  return (
    <svg className="signin-network" viewBox="0 0 1200 900" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <g className="net-lines">
        {lines.map(([x1, y1, x2, y2]) => (
          <line key={`${x1}-${y1}-${x2}-${y2}`} x1={x1} y1={y1} x2={x2} y2={y2} />
        ))}
      </g>
      <g className="net-nodes">
        {nodes.map(([cx, cy, r]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
        ))}
      </g>
      <g className="net-stars">
        <path d="M600 430L602.2 438.8L611 441L602.2 443.2L600 452L597.8 443.2L589 441L597.8 438.8Z" />
        <path d="M490 513L491.6 518.4L497 520L491.6 521.6L490 527L488.4 521.6L483 520L488.4 518.4Z" />
        <path d="M710 513L711.6 518.4L717 520L711.6 521.6L710 527L708.4 521.6L703 520L708.4 518.4Z" />
      </g>
    </svg>
  );
}

function Star({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="currentColor" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

/** Google's "G", in its own colours. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
      <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 010-4.18V7.07H2.18a11 11 0 000 9.86l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 002.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
